import { useCallback, useState } from "react";

/// Versioned localStorage prefs for form smart defaults (rule F6).
///
/// - Keys are namespaced `pw-form:<key>:<version>` so a schema bump
///   ignores stale shapes instead of crashing.
/// - All `localStorage` access wraps in try/catch (Safari/Firefox private
///   browsing, quota exceeded, or storage disabled). Must not break a form.
/// - Persist only the few fields you actually want sticky. Never persist
///   recipients, tokens, or PII (rule FE-SEC3).

export interface FormPrefSchema<T> {
  /// Stable key (slug-style). Final localStorage key is
  /// `pw-form:<key>:<version>`.
  key: string;
  /// Bump when the persisted shape changes. Old values become unread.
  version: number;
  /// Returned when nothing is persisted or parsing fails.
  fallback: T;
  /// Optional runtime guard so a malformed value (manual edit, schema
  /// drift) falls back instead of corrupting state.
  validate?: (value: unknown) => value is T;
}

function storageKey(schema: FormPrefSchema<unknown>): string {
  return `pw-form:${schema.key}:${schema.version}`;
}

export function readFormPref<T>(schema: FormPrefSchema<T>): T {
  if (typeof window === "undefined") return schema.fallback;
  try {
    const raw = window.localStorage.getItem(storageKey(schema));
    if (raw === null) return schema.fallback;
    const parsed = JSON.parse(raw) as unknown;
    if (schema.validate && !schema.validate(parsed)) return schema.fallback;
    return parsed as T;
  } catch {
    return schema.fallback;
  }
}

export function writeFormPref<T>(schema: FormPrefSchema<T>, value: T): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(storageKey(schema), JSON.stringify(value));
  } catch {
    // Quota exceeded / disabled storage: silently noop. Smart defaults
    // are an enhancement, not a correctness guarantee.
  }
}

export function clearFormPref(schema: FormPrefSchema<unknown>): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(storageKey(schema));
  } catch {
    // ignore
  }
}

/// React hook around `readFormPref` / `writeFormPref`. The initial state
/// uses lazy initialization so the storage read only happens on mount
/// (Vercel rule 5.12). Returns `[value, setValue]` like `useState`, where
/// `setValue` both updates React state and persists.
export function useFormPref<T>(schema: FormPrefSchema<T>): [T, (next: T) => void] {
  const [value, setValue] = useState<T>(() => readFormPref(schema));

  const set = useCallback(
    (next: T) => {
      setValue(next);
      writeFormPref(schema, next);
    },
    // schema is expected to be a module-level constant; deps stable.
    // reason: callback should never re-create across renders of the host
    [schema],
  );

  return [value, set];
}
